import React, { useState, useCallback, useEffect } from "react";
import TeamsGroupsList from "./TeamsGroupsList";
import TeamDetails from "./TeamDetails";
import {
  getTeamsApi,
  getTeamByIdApi,
  createTeamApi,
  updateTeamApi,
  deleteTeamApi,
  addTeamMemberApi,
  updateTeamMemberRoleApi,
  removeTeamMemberApi,
  createTeamChannelApi,
  updateChannelApi,
  getChannelMembersApi,
  addChannelMemberApi,
  removeChannelMemberApi,
  getDepartmentsApi,
  getUsersApi,
  getTeamAuditLogsApi,
} from "../../../../api/api";

function TeamsGroups() {
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchTeams();
    fetchDepartments();
    fetchUsers();
  }, []);

  const fetchTeams = async (filters = {}) => {
    try {
      setLoading(true);
      const response = await getTeamsApi(
        filters.page || 1,
        filters.limit || 20,
        filters.search || "",
        filters.department || "",
        filters.type || "",
        filters.status || "",
      );
      if (response.data?.success) {
        setTeams(response.data.teams || response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch teams:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await getDepartmentsApi();
      if (response.data?.success) {
        setDepartments(response.data.departments || response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch departments:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsersApi(1, 100, "");
      if (response.data?.success) {
        setUsers(response.data.users || response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  const fetchTeamDetails = async (teamId) => {
    try {
      setDetailsLoading(true);
      const response = await getTeamByIdApi(teamId);
      if (response.data?.success) {
        setSelectedTeam(response.data.team || response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch team details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  // ── KEY FIX: Immediately show details view with loading, then fetch ──
  const handleOpenTeam = useCallback((team) => {
    // 1. Set the selected team ID immediately (triggers details view)
    setSelectedTeamId(team.id);
    // 2. Set basic team info from list data so details page has something to show
    setSelectedTeam(team);
    // 3. Fetch full details in background
    fetchTeamDetails(team.id);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedTeamId(null);
    setSelectedTeam(null);
    fetchTeams();
  }, []);

  const handleUpdateTeam = useCallback(async (updatedTeam) => {
    try {
      const response = await updateTeamApi(updatedTeam.id, {
        name: updatedTeam.name,
        description: updatedTeam.description,
        departmentId: updatedTeam.departmentId,
        privacyType: updatedTeam.type || updatedTeam.privacyType,
        status: updatedTeam.status,
        permissions: updatedTeam.permissions,
      });
      if (response.data?.success) {
        fetchTeamDetails(updatedTeam.id);
        setTeams((prev) =>
          prev.map((t) =>
            t.id === updatedTeam.id ? { ...t, ...response.data.team } : t,
          ),
        );
      }
    } catch (err) {
      console.error("Failed to update team:", err);
    }
  }, []);

  const handleCreateTeam = useCallback(
    async (formData) => {
      try {
        const payload = {
          name: formData.name,
          description: formData.description,
          departmentId: formData.departmentId,
          ownerUserId: formData.owner,
          privacyType: formData.privacyType,
          initialMemberIds: formData.initialMembers || [],
          permissions: {
            whoCanPost: "All Members",
            whoCanUploadFiles: "All Members",
            whoCanCreateMeetings: "Owner/Admin Only",
            whoCanInviteUsers: "Owner/Admin Only",
            whoCanDeleteMessages: "Owner/Admin Only",
          },
        };
        const response = await createTeamApi(payload);
        if (response.data?.success) {
          fetchTeams();
        }
      } catch (err) {
        console.error("Failed to create team:", err);
      }
    },
    [fetchTeams],
  );

  const handleDeleteTeam = useCallback(
    async (teamId) => {
      try {
        await deleteTeamApi(teamId);
        setTeams((prev) => prev.filter((t) => t.id !== teamId));
        handleBack();
      } catch (err) {
        console.error("Failed to delete team:", err);
      }
    },
    [handleBack],
  );

  const handleAddMember = useCallback(async (teamId, formData) => {
    try {
      const response = await addTeamMemberApi(teamId, {
        userId: formData.userId,
        role: formData.role,
      });
      if (response.data?.success) fetchTeamDetails(teamId);
    } catch (err) {
      console.error("Failed to add member:", err);
    }
  }, []);

  const handleUpdateMemberRole = useCallback(async (teamId, userId, role) => {
    try {
      await updateTeamMemberRoleApi(teamId, userId, { role });
      fetchTeamDetails(teamId);
    } catch (err) {
      console.error("Failed to update member role:", err);
    }
  }, []);

  const handleRemoveMember = useCallback(async (teamId, userId) => {
    try {
      await removeTeamMemberApi(teamId, userId);
      fetchTeamDetails(teamId);
    } catch (err) {
      console.error("Failed to remove member:", err);
    }
  }, []);

  const handleCreateChannel = useCallback(async (teamId, formData) => {
    try {
      await createTeamChannelApi(teamId, {
        name: formData.name,
        channelType: formData.type,
        whoCanPost: formData.whoCanPost,
      });
      fetchTeamDetails(teamId);
    } catch (err) {
      console.error("Failed to create channel:", err);
    }
  }, []);

  const handleArchiveChannel = useCallback(
    async (channelId, status) => {
      try {
        const newStatus = status === "Active" ? "Archived" : "Active";
        await updateChannelApi(channelId, { status: newStatus });
        if (selectedTeamId) fetchTeamDetails(selectedTeamId);
      } catch (err) {
        console.error("Failed to update channel:", err);
      }
    },
    [selectedTeamId],
  );

  const handleGetChannelMembers = useCallback(async (channelId) => {
    try {
      const response = await getChannelMembersApi(channelId);
      return response.data?.success
        ? response.data.members || response.data.data
        : [];
    } catch (err) {
      console.error("Failed to get channel members:", err);
      return [];
    }
  }, []);

  const handleAddChannelMember = useCallback(async (channelId, userId) => {
    try {
      await addChannelMemberApi(channelId, { userId });
      return true;
    } catch (err) {
      console.error("Failed to add channel member:", err);
      return false;
    }
  }, []);

  const handleRemoveChannelMember = useCallback(async (channelId, userId) => {
    try {
      await removeChannelMemberApi(channelId, userId);
      return true;
    } catch (err) {
      console.error("Failed to remove channel member:", err);
      return false;
    }
  }, []);

  const handleGetTeamAuditLogs = useCallback(async (teamId, page = 1) => {
    try {
      const response = await getTeamAuditLogsApi(teamId, page);
      return response.data?.success ? response.data : null;
    } catch (err) {
      console.error("Failed to get audit logs:", err);
      return null;
    }
  }, []);

  // Team details view - shows immediately with loading state
  if (selectedTeam) {
    return (
      <TeamDetails
        team={selectedTeam}
        departments={departments}
        users={users}
        loading={detailsLoading}
        fetchTeamDetails={fetchTeamDetails}
        onBack={handleBack}
        onUpdateTeam={handleUpdateTeam}
        onDeleteTeam={handleDeleteTeam}
        onAddMember={handleAddMember}
        onUpdateMemberRole={handleUpdateMemberRole}
        onRemoveMember={handleRemoveMember}
        onCreateChannel={handleCreateChannel}
        onArchiveChannel={handleArchiveChannel}
        onGetChannelMembers={handleGetChannelMembers}
        onAddChannelMember={handleAddChannelMember}
        onRemoveChannelMember={handleRemoveChannelMember}
        onGetTeamAuditLogs={handleGetTeamAuditLogs}
      />
    );
  }

  // List view
  return (
    <TeamsGroupsList
      teams={teams}
      departments={departments}
      users={users}
      loading={loading}
      onOpenTeam={handleOpenTeam}
      onCreateTeam={handleCreateTeam}
      onUpdateTeam={handleUpdateTeam}
      onRefresh={() => fetchTeams()}
    />
  );
}

export default TeamsGroups;
